import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Card} from "../../../../../components/card/card.jsx";
import {RoleContext} from "../context/role-context.jsx";
import Button from "../../../../../components/button/button.jsx";
import {Tree} from "primereact/tree";
import InputForm from "../../../../../components/input/input-form.jsx";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import roleService from "../../../../../services/web/role-service.js";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import projectUtils from "../../../../../utils/project-utils.js";
import "../../../../../components/tree-user/tree-users.css"

const API_URL = import.meta.env.VITE_API_URL;

const RoleUsers = () => {
    const {permissionsSelected, activeUsers, setActiveUsers, inativeUsers} = useContext(RoleContext);
    const navigate = navigateHook();

    const [usersTree, setUsersTree] = useState([]);
    const [open, setOpen] = useState(false);
    const [userData, setUserData] = useState([]);
    const [remove, setRemove] = useState(false)

    const {data: users} = useQuery({
        queryFn: () => roleService.activeUsers(),
        queryKey: ["users"],
    });

    const gridRef = useRef(undefined);

    const [columnDefs,] = useState([
        {headerName: "Nome", field: "name", flex: 1, comparator: aggridUtils.nullLastComparator},
        {headerName: "Nome de guerra", field: "namewar", flex: 1, comparator: aggridUtils.nullLastComparator}
    ]);

    const rowSelection = useMemo(() => {
        return {
            mode: 'multiRow',
            checkboxes: true,
            headerCheckbox: false,
            enableSelectionWithoutKeys: true,
            enableClickSelection: true,
        };
    }, []);

    const defaultColDef = useMemo(() => {
        return {
            filter: true,
            floatingFilter: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    useEffect(() => {
        const prepareUsersToTree = (users) => {
            if (users.length === 0) {
                return [{key: -1, label: `Não há agentes`, children: []}];
            }

            return users.map(user => {
                return {key: user.id, label: `${user.name} (${user.login ?? ""})`, children: []};
            });
        }

        const activeUsersTree = prepareUsersToTree(activeUsers);
        const inativeUsersTree = prepareUsersToTree(inativeUsers);

        const tree = [
            {
                key: `active`,
                label: `Ativos (${activeUsers.length})`,
                children: activeUsersTree,
            },
            {
                key: `inactive`,
                label: `Inativos (${inativeUsers.length})`,
                children: inativeUsersTree,
            }
        ];

        setUsersTree(tree);
    }, [activeUsers, inativeUsers]);

    const filterTemplate = (node) => {
        return (
            <InputForm
                id={"agent-search"}
                label={"Pesquisar agentes"}
                title={"search"}
                onChange={node.filterInputChange}
                onKeyDown={node.filterInputKeyDown}
            />
        );
    }

    const templateToTree = (node) => {
        const {children, label, key} = node;

        if (children.length > 0) {
            return (
                <div className={"p-tree-node p-tree-node-father"}>
                    <span className="p-tree-node-label">
                        {label}
                    </span>
                </div>
            );
        }

        if (key === -1) {
            return (
                <div key={key}>
                    <span>{label}</span>
                </div>
            );
        }

        const accessUser = () => navigate("/user-agents/form/" + key, true);

        return (
            <div className={"user"} key={key}>
                <a className={"flex gap-2 cursor-pointer !text-[#676a6c] font-bold hover:!text-[#386bc0]"} onClick={() => accessUser()} target={"_blank"}>
                    <div className="img-thumbnail" style={{backgroundImage: `url(${API_URL}/user/getImage/${key})`}}></div>
                    <span>{label}</span>
                </a>
            </div>
        );
    }

    const removeBtn = () => {
        setUserData(activeUsers);
        setRemove(true);
        setOpen(true);
    }

    const addBtn = () => {
        setUserData(users.filter(user => !activeUsers.some(activeUser => activeUser.id === user.id)));
        setRemove(false);
        setOpen(true);
    }

    const save = () => {
        const usersSelected = []
        gridRef.current.api.forEachNode(node => {
            if (node.isSelected()) {
                usersSelected.push(node.data)
            }
        })

        if (remove) {
            removeUsers(usersSelected);
            return;
        }

        const newList = [...activeUsers, ...usersSelected].sort((a,b) => {
            return a["name"] > b["name"] ? 1 : -1;
        });

        setActiveUsers(newList);
        setUserData([]);
        setOpen(false);
    }

    const removeUsers = async (usersSelected) => {
        let hasReturn = false;
        const promises = [];

        const usersId = usersSelected.map(item => item.id);
        const find = permissionsSelected.find(p => p.name === PermissionEnum.DISPATCH_GARRISON_LEADER);
        if (find) {
            const promise = roleService.dispatchDependencies({permissionId: find.id, users: usersId})
                .then((data) => {
                    if (data.length) {
                        hasReturn = true;

                        projectUtils.swalErrorMessage(
                            'Erro',
                            "Usuário é responsável por uma guarnição ativa. Não é possível remover essa permissão.",
                            'Ok'
                        );
                    }
                })
                .catch(error => {
                    console.log(error);
                    hasReturn = false;
                });

            promises.push(promise);
        }

        await Promise.all(promises);

        if (!hasReturn) {
            setActiveUsers(activeUsers.filter(u => !usersId.some(id => id === u.id)));
            setOpen(false);
        }
    }

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Title>Agentes</Card.Title>
                </Card.Header>
                <Card.Body className={"h-[calc(100vh-282px)] overflow-auto"}>
                    <Tree
                        filter
                        filterTemplate={filterTemplate}
                        value={usersTree}
                        expandedKeys={{'active': true, 'inactive': true}}
                        nodeTemplate={templateToTree}
                    />
                </Card.Body>
                <Card.Footer className={"flex gap-2 !justify-end"}>
                    <Button target={"danger"} onClick={removeBtn}>
                        Remover agentes
                    </Button>
                    <Button onClick={addBtn}>
                        Adicionar agentes
                    </Button>
                </Card.Footer>
            </Card>

            <Modal open={open} setOpen={setOpen} title={(remove ? "Remover" : "Adicionar") + " agentes"} className={"w-8/12"} overflow={true}>
                <Modal.Body className={"space-y-2"}>
                    <div style={{height: "calc(100vh - 300px)"}}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={userData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            rowSelection={rowSelection}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            suppressRowVirtualisation={true}
                            suppressColumnVirtualisation={true}
                            theme={aggridUtils.generalTheme()}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={save}>
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RoleUsers;